import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Wind, Waves, Cog, CircleDot, Shield, TrendingDown } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const ErosionWear = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const erosionTypes = [
    {
      title: 'Abrasive Wear',
      description: 'Material loss from hard particles sliding or rolling across surfaces under pressure.',
      icon: CircleDot,
      causes: ['Sand and grit in flow streams', 'Solids handling operations', 'Conveyor systems'],
      affected: ['Pump impellers', 'Pipe elbows', 'Chutes and hoppers', 'Cyclones']
    },
    {
      title: 'Erosion-Corrosion',
      description: 'Accelerated attack when corrosive fluid flow removes protective oxide layers.',
      icon: Waves,
      causes: ['High-velocity fluid flow', 'Turbulent conditions', 'Corrosive media'],
      affected: ['Heat exchangers', 'Condensers', 'Piping systems', 'Valves']
    },
    {
      title: 'Cavitation Erosion',
      description: 'Damage from imploding vapor bubbles in low-pressure zones of flowing liquids.',
      icon: Wind,
      causes: ['Pump suction issues', 'Valve throttling', 'Propeller operation'],
      affected: ['Pump housings', 'Valve bodies', 'Propellers', 'Turbines']
    },
    {
      title: 'Sliding Wear',
      description: 'Surface degradation from metal-to-metal contact in moving components.',
      icon: Cog,
      causes: ['Bearing surfaces', 'Shaft journals', 'Gear teeth contact'],
      affected: ['Bearings', 'Shafts', 'Gearboxes', 'Slides and ways']
    }
  ];

  const protectionMethods = [
    {
      title: 'Ceramic Coatings',
      description: 'Ultra-hard ceramic-filled composites for severe abrasion environments.',
      benefit: 'Up to 10x longer life than bare metal'
    },
    {
      title: 'Rubber Linings',
      description: 'Elastomeric protection that absorbs impact and resists sliding wear.',
      benefit: 'Ideal for slurry and solids handling'
    },
    {
      title: 'Metal Rebuilding',
      description: 'Restore worn surfaces to original dimensions with wear-resistant alloys.',
      benefit: 'Return equipment to like-new condition'
    },
    {
      title: 'Polymer Composites',
      description: 'Engineered materials combining corrosion and erosion resistance.',
      benefit: 'Versatile protection for varied conditions'
    }
  ];

  const wearIndicators = [
    'Reduced equipment efficiency',
    'Increased vibration or noise',
    'Visible surface roughening',
    'Dimensional changes or clearance loss',
    'Contaminated product or process',
    'Higher energy consumption'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Erosion & Wear Protection" description="Erosion and wear protection for industrial equipment. Ceramic-filled and rubber-based coating systems." path="/research/erosion-wear" />
      
      <PageHero
        badge="Research & Learn"
        title="Erosion & Wear"
        description="Erosion and wear silently degrade your equipment performance. Understanding these mechanisms helps you implement effective protection before costly failures occur."
        backgroundKey="erosion-wear"
        buttonText="Get Wear Assessment"
      />

      {/* Erosion Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Types of Erosion & Wear</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {erosionTypes.map((type, index) => {
              const IconComponent = type.icon;
              return (
                <Card key={index} className="border-l-4 border-l-amber-600">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <IconComponent className="text-amber-600 mr-3" size={28} />
                      {type.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{type.description}</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-slate-700 mb-2">Common Causes:</p>
                        <ul className="text-gray-600 space-y-1">
                          {type.causes.map((cause, idx) => (
                            <li key={idx}>• {cause}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700 mb-2">Equipment Affected:</p>
                        <ul className="text-gray-600 space-y-1">
                          {type.affected.map((item, idx) => (
                            <li key={idx}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Protection Methods */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Protection Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {protectionMethods.map((method, index) => (
              <Card key={index} className="text-center">
                <CardHeader>
                  <Shield className="mx-auto text-green-700 mb-2" size={32} />
                  <CardTitle className="text-lg">{method.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4">{method.description}</p>
                  <Badge className="bg-green-100 text-green-800">{method.benefit}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Wear Indicators */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Signs of Wear Damage</h2>
              <p className="text-gray-600 mb-8">
                Erosion and wear often progress gradually, making early detection challenging. 
                Watch for these indicators that suggest protective action is needed.
              </p>
              <div className="space-y-3">
                {wearIndicators.map((indicator, index) => (
                  <div key={index} className="flex items-center bg-amber-50 p-3 rounded-lg">
                    <TrendingDown className="text-amber-600 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{indicator}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-amber-700 text-white p-8 rounded-lg">
              <Cog className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Extend Equipment Life</h3>
              <p className="text-amber-100 mb-6">
                Proper wear protection can extend equipment life by 3-10x compared to unprotected surfaces. 
                The investment typically pays back within months through reduced replacement and downtime costs.
              </p>
              <Button className="bg-white text-amber-700 hover:bg-gray-100" onClick={() => navigate('/services/protective-coatings')}>
                View Coating Solutions <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Stop Wear Before It Stops You</h2>
          <p className="text-blue-100 mb-8">
            Our wear specialists can analyze your application and recommend the optimal protection strategy.
          </p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Request Wear Analysis <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ErosionWear;
